import React, { useEffect, useRef, useState } from 'react';
import {
  Button, ButtonToolbar, Divider, Form, InputNumber, Radio, RadioGroup, Schema, SelectPicker,
  Stack,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import ReloadIcon from '@rsuite/icons/Reload';
import { useNavigate } from 'react-router-dom';
import { getInventories } from './BaselinkerClient';
import { getEshopCodesUsingBaselinkerDefaultConfig } from '../api/baselinker/baselinker';
import { BaselinkerPriceSyncTarget } from '../api/apiTypes';
import handleError from '../api/apiErrorHandle';
import {
  BaselinkerIntegrationInfoFormValue,
  EMPTY_BASELINKER_INTEGRATION_INFO,
  isB2bPriceMode,
  loadBaselinkerIntegrationInfo,
  saveBaselinkerIntegrationInfo,
} from './baselinkerIntegrationInfoService';
import { notificationError, notificationSuccess, validationError } from '../services/notificationActions';
import CustomFieldsForm from './CustomFieldsForm';
import BasicInformationForm from './BasicInformationForm';
import OrderStatusesForm from './OrderStatusesForm';
import PlatformIdSync from './PlatformIdSync';
import ApiTokenReset from '../mpi/ApiTokenReset';
import { CURRENCIES } from '../utils/currencies';

const CURRENCY_OPTIONS = CURRENCIES.map(currency => ({ label: currency, value: currency }));
const MIN_VAT_PERCENTAGE = 0;
const MAX_VAT_PERCENTAGE = 100;
const VAT_PERCENTAGE_PATTERN = /^\d+(\.\d{1,2})?$/;

const B2B_PRICE_CURRENCY_RULE = Schema.Types.StringType()
  .isRequired('This field is required.');

const B2B_PRICE_VAT_RULE = Schema.Types.NumberType('VAT must be a number.')
  .isRequired('This field is required.')
  .addRule(
    (value) => Number(value) >= MIN_VAT_PERCENTAGE && Number(value) <= MAX_VAT_PERCENTAGE,
    `VAT must be at least ${MIN_VAT_PERCENTAGE} and at most ${MAX_VAT_PERCENTAGE}.`,
  )
  .addRule(
    (value) => VAT_PERCENTAGE_PATTERN.test(`${value}`),
    'At most 2 decimal places are allowed.',
  );

interface BaseLinkerIntegrationFormProps {
  onSave: (blApiToken: string) => void;
}

export default function BaseLinkerIntegrationForm({ onSave }: BaseLinkerIntegrationFormProps) {
  const navigate = useNavigate();
  const formRef = useRef<any>();
  const [blLinked, setBlLinked] = useState<boolean>(false);
  const [formValue, setFormValue] = useState<BaselinkerIntegrationInfoFormValue>(
    EMPTY_BASELINKER_INTEGRATION_INFO,
  );
  const [eshopCodes, setEshopCodes] = useState<string[]>([]);

  const b2bPriceMode = isB2bPriceMode(formValue.priceSyncTarget);

  const apiTokenRule = Schema.Types.StringType()
    .isRequired('This field is required.')
    .addRule(() => blLinked, 'Click the button to link BaseLinker account');

  async function linkBl(blApiToken: string): Promise<void> {
    try {
      await getInventories(blApiToken);
      setBlLinked(true);
      notificationSuccess('Connected to BaseLinker');
    } catch (error: any) {
      setBlLinked(false);
      notificationError(error.message);
    }
  }

  useEffect(() => {
    formRef.current?.checkForField('blApiToken');
  }, [blLinked]);

  async function loadFormValue(): Promise<void> {
    try {
      const integrationInfo = await loadBaselinkerIntegrationInfo();
      setFormValue(integrationInfo);

      if (integrationInfo.blApiToken) {
        await linkBl(integrationInfo.blApiToken);
      }
    } catch (error) {
      handleError(error);
    }
  }

  useEffect(() => {
    loadFormValue();
    getEshopCodesUsingBaselinkerDefaultConfig()
      .then((data) => setEshopCodes(data));
  }, []);

  async function handleSubmit(): Promise<void> {
    if (!formRef.current?.check()) {
      validationError('Please check the form there are validation errors.');
      return;
    }

    try {
      setFormValue(await saveBaselinkerIntegrationInfo(formValue));
      notificationSuccess('BaseLinker setup saved');
      onSave(formValue.blApiToken);
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <div>
      <br />
      <Form
        ref={formRef}
        formValue={formValue}
        layout="horizontal"
        onChange={value => setFormValue(value as BaselinkerIntegrationInfoFormValue)}
      >
        <Form.Group controlId="blApiToken">
          <Form.ControlLabel>BL API token</Form.ControlLabel>
          <Stack spacing={15}>
            <Form.Control name="blApiToken" rule={apiTokenRule} />
            <Button
              appearance="primary"
              onClick={() => linkBl(formValue.blApiToken)}
              disabled={!formValue.blApiToken}
            >
              Link
              {' '}
              <Icon as={ReloadIcon} hidden={!blLinked} />
            </Button>
          </Stack>
        </Form.Group>
        <Form.Group controlId="priceSyncTarget">
          <Form.ControlLabel>Price from BaseLinker is</Form.ControlLabel>
          <Form.Control inline name="priceSyncTarget" accepter={RadioGroup}>
            <Radio value={BaselinkerPriceSyncTarget.SALES_PRICE}>Sales price</Radio>
            <Radio value={BaselinkerPriceSyncTarget.B2B_PRICE}>B2B price</Radio>
          </Form.Control>
        </Form.Group>
        {b2bPriceMode && (
        <>
          <Form.Group controlId="b2bPriceCurrency">
            <Form.ControlLabel>B2B price currency</Form.ControlLabel>
            <Form.Control
              name="b2bPriceCurrency"
              accepter={SelectPicker}
              data={CURRENCY_OPTIONS}
              searchable={false}
              cleanable={false}
              rule={B2B_PRICE_CURRENCY_RULE}
            />
            <Form.HelpText tooltip>Required</Form.HelpText>
          </Form.Group>
          <Form.Group controlId="b2bPriceVat">
            <Form.ControlLabel>B2B price VAT</Form.ControlLabel>
            <Form.Control
              name="b2bPriceVat"
              accepter={InputNumber}
              min={MIN_VAT_PERCENTAGE}
              max={MAX_VAT_PERCENTAGE}
              postfix="%"
              rule={B2B_PRICE_VAT_RULE}
            />
            <Form.HelpText tooltip>
              Required. Percentage, not a coefficient (21 = 21 %).
            </Form.HelpText>
          </Form.Group>
        </>
        )}
        <Form.Group>
          <ButtonToolbar>
            <Button appearance="primary" onClick={() => handleSubmit()}>Submit</Button>
            <Button appearance="ghost" onClick={() => navigate(-1)}>Cancel</Button>
          </ButtonToolbar>
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <ApiTokenReset />
          </ButtonToolbar>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel />
          {eshopCodes.length > 0 && (
          <PlatformIdSync
            show
            eshopCodes={eshopCodes}
          />
          )}
        </Form.Group>
      </Form>
      <Divider>Custom Fields</Divider>
      <CustomFieldsForm blLinked={blLinked} blApiToken={formValue.blApiToken} />
      <Divider>Basic information</Divider>
      <BasicInformationForm blLinked={blLinked} blApiToken={formValue.blApiToken} />
      <Divider>Order statuses</Divider>
      <OrderStatusesForm blLinked={blLinked} blApiToken={formValue.blApiToken} />
    </div>
  );
}
